/*import React from "react";

const WhyChooseUs = () => {
  const features = [
    {
      title: "Safe & Comfortable Rides",
      description:
        "Experienced drivers and sanitized vehicles to ensure your safety and comfort.",
      icon: (
        <svg
          className="w-8 h-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 10h1l1 2h12l1-2h1M5 16h14l1 2H4l1-2z"
          ></path>
        </svg>
      ),
    },
    {
      title: "Affordable Pricing",
      description: "Transparent fares with no hidden charges to fit your budget.",
      icon: (
        <svg
          className="w-8 h-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8c-3 0-5 1.5-5 4s2 4 5 4 5-1.5 5-4-2-4-5-4z"
          ></path>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 12v4"
          ></path>
        </svg>
      ),
    },
    {
      title: "24/7 Availability",
      description: "Book a cab anytime, day or night, we’re always here.",
      icon: (
        <svg
          className="w-8 h-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 8v4l3 3"
          ></path>
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
        </svg>
      ),
    },
    {
      title: "Easy Booking Process",
      description:
        "Quick and hassle-free ride booking with our intuitive interface.",
      icon: (
        <svg
          className="w-8 h-8 text-blue-600"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M9 5l7 7-7 7"
          ></path>
        </svg>
      ),
    },
  ];

  return (
    <section className="py-16 bg-gray-50 px-6 lg:px-24">
      <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">
        Why Choose Us
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-5xl mx-auto">
        {features.map(({ title, description, icon }, idx) => (
          <div
            key={idx}
            className="flex items-start space-x-4 bg-white p-6 rounded-lg shadow-md hover:shadow-lg transition"
          >
            <div>{icon}</div>
            <div>
              <h3 className="text-xl font-semibold mb-2 text-gray-900">{title}</h3>
              <p className="text-gray-600">{description}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default WhyChooseUs;*/


import { motion, useInView } from 'framer-motion';
import { Shield, Clock, DollarSign, Star, Headphones, MapPin, Zap, Award } from 'lucide-react';
import { useRef } from 'react';

const features = [
  {
    icon: Shield,
    title: 'Safe & Verified',
    description: 'All our drivers are carefully verified and trained to ensure your safety on every ride.',
    color: 'from-blue-500 to-cyan-500',
    stat: '100%',
    statLabel: 'Verified'
  },
  {
    icon: Clock,
    title: '24/7 Availability',
    description: 'Book your ride anytime, anywhere — we’re always ready to serve you around the clock.',
    color: 'from-teal-500 to-cyan-500',
    stat: '24/7',
    statLabel: 'Support'
  },
  {
    icon: DollarSign,
    title: 'Best Prices',
    description: 'Enjoy competitive fares with transparent pricing and no hidden charges.',
    color: 'from-blue-500 to-blue-400',
    stat: '40%',
    statLabel: 'Save'
  },
  {
    icon: Star,
    title: 'Premium Experience',
    description: 'Travel in style with luxury vehicles and service that exceeds expectations.',
    color: 'from-yellow-500 to-orange-500',
    stat: '4.9★',
    statLabel: 'Rating'
  },
  {
    icon: Headphones,
    title: 'Customer Support',
    description: 'Our dedicated support team is always ready to assist you promptly and effectively.',
    color: 'from-red-500 to-pink-500',
    stat: '<5 min',
    statLabel: 'Response Time'
  },
  {
    icon: MapPin,
    title: 'Wide Coverage',
    description: 'Serving 500+ destinations with reliable transport options wherever you need.',
    color: 'from-indigo-500 to-purple-500',
    stat: '500+',
    statLabel: 'Cities'
  },
];

const WhyChooseUs = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div className="py-24 bg-gradient-to-b from-white via-teal-50/30 to-white relative overflow-hidden">
      {/* Animated subtle background pattern */}
      <motion.div
        className="absolute inset-0 opacity-30"
        animate={{
          backgroundPosition: ['0% 0%', '100% 100%'],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          repeatType: 'reverse',
        }}
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(59,130,246,0.05) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 -mt-10"
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            whileInView={{ scale: 1, rotate: 0 }}
            viewport={{ once: true }}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="inline-block px-6 py-3 bg-gradient-to-r from-blue-100 to-blue-100 text-blue-900 rounded-full mb-6 shadow-lg"
          >
            <Award className="w-5 h-5 inline mr-2" />
            Why Choose Us
          </motion.div>
          <motion.h2 
            className="mb-6 text-3xl font-semibold text-blue-900"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
          >
            Experience Excellence in Every Journey
          </motion.h2>
          <motion.p 
            className="text-xl text-blue-800 max-w-3xl mx-auto"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.4 }}
          >
            We provide the best cab booking and tour experience with unmatched quality, safety, and customer service.
          </motion.p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 50, rotateX: -20 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ 
                duration: 0.6, 
                delay: index * 0.1,
                type: "spring",
                stiffness: 100
              }}
              whileHover={{ 
                y: -15,
                rotateY: 5,
                transition: { duration: 0.3 }
              }}
              className="group"
            >
              <div className="relative p-8 rounded-3xl bg-white hover:shadow-2xl border border-gray-100 transition-all duration-500 h-full overflow-hidden">
                {/* Gradient Background on Hover */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-0 group-hover:opacity-10 rounded-3xl transition-opacity duration-500`}
                />

                {/* Shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/50 to-transparent"
                  initial={{ x: '-100%' }}
                  whileHover={{ x: '200%' }}
                  transition={{ duration: 0.6 }}
                />

                <div className="relative">
                  {/* Icon with animated floating & rotation on hover */}
                  <motion.div
                    className="inline-block mb-6 relative"
                    animate={{
                      y: isInView ? [0, -10, 0] : 0,
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: index * 0.2,
                      ease: "easeInOut"
                    }}
                  >
                    <motion.div
                      className={`inline-flex p-5 rounded-2xl bg-gradient-to-br ${feature.color} shadow-lg`}
                      whileHover={{ scale: 1.15, rotate: 360 }}
                      transition={{ type: "spring", stiffness: 200, duration: 0.6 }}
                    >
                      <feature.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    
                    {/* Stat badge */}
                    <motion.div
                      className="absolute -top-2 -right-2 bg-white rounded-full px-3 py-1 shadow-lg border-2 border-gray-100"
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 + 0.3, type: "spring" }}
                      whileHover={{ scale: 1.2, rotate: 10 }}
                    >
                      <span className={`text-xs bg-gradient-to-r ${feature.color} bg-clip-text text-transparent`}>
                        {feature.stat}
                      </span>
                    </motion.div>
                  </motion.div>

                  <motion.h3 
                    className="mb-3 text-2xl group-hover:bg-gradient-to-r group-hover:from-teal-600 group-hover:to-cyan-600 group-hover:bg-clip-text group-hover:text-transparent transition-all duration-300"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.2 }}
                  >
                    {feature.title}
                  </motion.h3>
                  
                  <motion.p 
                    className="text-gray-600 mb-4"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.3 }}
                  >
                    {feature.description}
                  </motion.p>

                  {/* Stat label */}
                  <motion.div
                    className="flex items-center gap-2 text-sm text-gray-500"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 + 0.4 }}
                  >
                    <Zap className="w-4 h-4 text-teal-600" />
                    <span>{feature.statLabel}</span>
                  </motion.div>
                </div>

                {/* Animated corner decoration */}
                <motion.div
                  className={`absolute -bottom-10 -right-10 w-32 h-32 bg-gradient-to-br ${feature.color} opacity-10 rounded-full blur-2xl`}
                  animate={{
                    scale: [1, 1.2, 1],
                    opacity: [0.1, 0.2, 0.1],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: index * 0.3,
                  }}
                />

                {/* Border on hover */}
                <motion.div
                  className={`absolute inset-0 border-2 border-transparent group-hover:border-teal-200 rounded-3xl transition-all duration-500`}
                  initial={false}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Animated decorative divider */}
        <motion.div
          className="mt-24 flex items-center justify-center gap-4"
          initial={{ opacity: 0, scale: 0 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          <motion.div 
            className="h-1 w-32 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"
            animate={{
              scaleX: [1, 1.2, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
            }}
          />
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
          >
            <Star className="w-6 h-6 text-blue-600" />
          </motion.div>
          <motion.div 
            className="h-1 w-32 bg-gradient-to-r from-transparent via-blue-500 to-transparent rounded-full"
            animate={{
              scaleX: [1, 1.2, 1],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              delay: 1,
            }}
          />
        </motion.div>
      </div>
    </div>
  );
}

export default WhyChooseUs; 
